import { getTraceabilityThunk } from '@redux/features/traceability.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';
import DebouncedInput from 'Shared/DebouncedInput';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const Traceability: FC = () => {
    // -----------------------------------------------------------------
    const [page, setPage] = useState<number>(1);
    const [serialNumber, setSerialNumber] = useState<string>('');
    const [ndcCode, setNdcCode] = useState<string>('');
    const [fromDuns, setFromDuns] = useState<string>('');
    const [toDuns, setToDuns] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [dunsDateRange, setDunsDateRange] = useState<[Date | null, Date | null]>([null, null]);
    // -----------------------------------------------------------------

    const dispatch = useAppDispatch();
    const { traceability, loading } = useAppSelector((state: RootState) => state.traceabilityState);
    // -----------------------------------------------------------------

    const changePage = (p: string) => setPage(Number(p));
    // -----------------------------------------------------------------

    // Handle search functions (debounced)
    const handleSerialNumberSearch = (value: string) => {
        setSerialNumber(value);
        // setPage(1); // Reset to first page when searching
    };

    const handleNdcCodeSearch = (value: string) => {
        setNdcCode(value);
        // setPage(1); // Reset to first page when searching
    };

    // Helper function to format date to YYYY-MM-DDTHH:MM:SS format
    const formatDateForAPI = (date: Date, isEndDate: boolean = false): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        // Start date: 00:00:00, End date: 23:59:59 to cover entire day
        const time = isEndDate ? '23:59:59' : '00:00:00';
        return `${year}-${month}-${day}T${time}`;
    };

    // Handle date range changes - only send when both dates are selected
    const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
        setDateRange(dates);
        const [start, end] = dates;
        // Only update string dates when both start and end are selected
        if (start && end) {
            setStartDate(formatDateForAPI(start, false));
            setEndDate(formatDateForAPI(end, true));
        } else if (!start && !end) {
            // Clear both when range is cleared
            setStartDate('');
            setEndDate('');
        }
        // If only start is selected, don't update the string dates yet
    };

    const handleDunsDateRangeChange = (dates: [Date | null, Date | null]) => {
        setDunsDateRange(dates);
        const [start, end] = dates;
        // Only update string dates when both start and end are selected
        if (start && end) {
            setFromDuns(formatDateForAPI(start, false));
            setToDuns(formatDateForAPI(end, true));
        } else if (!start && !end) {
            // Clear both when range is cleared
            setFromDuns('');
            setToDuns('');
        }
        // If only start is selected, don't update the string dates yet
    };

    // -----------------------------------------------------------------

    useEffect(() => {
        dispatch(
            getTraceabilityThunk({
                size: 10,
                page: page - 1,
                serialNumber: serialNumber || '',
                ndcCode: ndcCode || '',
                fromDuns: fromDuns || '',
                toDuns: toDuns || '',
                startDate: startDate || '',
                endDate: endDate || '',
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, serialNumber, ndcCode, fromDuns, toDuns, startDate, endDate]);
    // -----------------------------------------------------------------

    return (
        <>
            <BreadCrumb pageTitle={`Traceability`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Traceability List</h3>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-5 flex-wrap">
                            <DebouncedInput
                                placeholder={`Search by Serial Number`}
                                onChange={(value) => {
                                    // Only update local state, don't trigger search immediately
                                    // The searchFn will handle the actual search after debounce
                                }}
                                searchFn={handleSerialNumberSearch}
                                searchValue={serialNumber}
                            />
                            <DebouncedInput
                                placeholder={`Search by NDC Code`}
                                onChange={(value) => {
                                    // Only update local state, don't trigger search immediately
                                    // The searchFn will handle the actual search after debounce
                                }}
                                searchFn={handleNdcCodeSearch}
                                searchValue={ndcCode}
                            />
                        </div>
                        <div className="flex gap-5 flex-wrap">
                            {/* Start Date - End Date Range Picker */}
                            <div className="flex-1 min-w-[300px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">Date Range</label>
                                <DatePicker
                                    selected={dateRange[0]}
                                    onChange={handleDateRangeChange}
                                    startDate={dateRange[0]}
                                    endDate={dateRange[1]}
                                    selectsRange
                                    isClearable
                                    placeholderText="Select date range"
                                    className="form-input border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                                    dateFormat="yyyy-MM-dd"
                                />
                            </div>
                            {/* From DUNS - To DUNS Date Range Picker */}
                            {/* <div className="flex-1 min-w-[300px]">
                                <label className="mb-2 block text-sm font-medium text-slate-700">DUNS Date Range</label>
                                <DatePicker
                                    selected={dunsDateRange[0]}
                                    onChange={handleDunsDateRangeChange}
                                    startDate={dunsDateRange[0]}
                                    endDate={dunsDateRange[1]}
                                    selectsRange
                                    isClearable
                                    placeholderText="Select date range"
                                    className="form-input border-slate-200 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 disabled:border-slate-300 disabled:text-slate-500 placeholder:text-slate-400 w-full transition-all duration-200"
                                    dateFormat="yyyy-MM-dd"
                                />
                            </div> */}
                        </div>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">NDC Code</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Serial Number</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">From DUNS</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">To DUNS</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Quantity</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={(traceability as any)?.content?.length ?? 0} animationKey={`trace-${page}`}>
                                            {(traceability as any)?.content?.map((item: any) => (
                                                <AnimatedTableRow
                                                    rowKey={item?.id}
                                                    detailRecord={item}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{item?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.ndcCode || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.serialNumber || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.fromDuns || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.toDuns || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{item?.quantity ?? `-`}</td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={(traceability as any)?.pageable?.pageSize || 10}
                                    total={(traceability as any)?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={
                                        (traceability as any)?.numberOfElements ||
                                        (traceability as any)?.content?.length ||
                                        0
                                    }
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Traceability;
